"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  Plus,
  Trash,
  Copy,
  Code,
  GripVertical,
  ChevronUp,
  ChevronsUpDown,
  ChevronsDownUp,
  EllipsisVertical,
  Text,
  Hash,
  ToggleRight,
  List,
  Braces,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { BindableInput } from "./BindableInput";
import { WHOLE_VAR_REF_PATTERN } from "./variableRefs";
import { parseFlowTypeLabel } from "@/components/SpotflowV2/flowTypes";
import { NoxusInput } from "@/components/Form/InputField";
import { SpotSelect } from "@/components/Form/SelectField";
import { CodeInput } from "@/components/Form/CodeField";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";

import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";

import { cn } from "@/lib/utils";

/**
 * V2 renderer for `display=ConfigStructuredFields`. A list of `{name, type,
 * value}` rows used to assemble a structured object: each row has a name input,
 * a type select, and a bindable value (literal or `:var[...]`). The picker
 * affordance + chip semantics come from {@link BindableInput}; the value's
 * `dataType` is derived from the row type so the picker filters compatible
 * variables. The list UX (drag reorder, accordion rows, expand/collapse all,
 * JSON mode, duplicate) mirrors `JsonSchemaBuilderConfig`.
 */

interface StructuredField {
  name: string;
  type: string;
  value: string;
}

const TYPE_ICONS: Record<string, LucideIcon> = {
  Text: Text,
  Number: Hash,
  Boolean: ToggleRight,
  List: List,
  Object: Braces,
};

// Map a field type to the picker's dataType so it filters compatible sources.
// List/Object accept any source (undefined = no filter).
const TYPE_TO_DATA_TYPE: Record<string, string | undefined> = {
  Text: "str",
  Number: "number",
  Boolean: "bool",
  List: undefined,
  Object: "object",
};

interface FieldTypeMeta {
  icon: LucideIcon | undefined;
  dataType: string | undefined;
  // List rows are bind-only: the value is an upstream list, never a literal.
  isList: boolean;
}

// The generic `List` is kept for rows saved before the typed `List of X`
// labels replaced it in the picker; both are lists to the value editor.
function fieldTypeMeta(typeValue: string): FieldTypeMeta {
  const typed = parseFlowTypeLabel(typeValue);
  if (typed?.isList) {
    return { icon: List, dataType: typed.dataType, isList: true };
  }
  return {
    icon: TYPE_ICONS[typeValue],
    dataType: TYPE_TO_DATA_TYPE[typeValue],
    isList: typeValue === "List",
  };
}

// Older configs may store the row type as a `{value, label}` chip object
// instead of a plain string — unwrap before rendering.
function typeValueOf(row: StructuredField): string {
  const raw = row.type as unknown;
  if (typeof raw === "object" && raw !== null && "value" in raw) {
    return String((raw as { value: unknown }).value ?? "");
  }
  return typeof raw === "string" ? raw : "";
}

const NAME_TOOLTIP_MIN_LENGTH = 200;

// Must exclude `visibility`: transitioned, it stays `visible` for the whole
// duration and the actions linger over the badge after the pointer leaves.
const ROW_ACTION_TRANSITION = "transition-[color,background-color,box-shadow]";

const TOOLTIP_NO_HOVER_STEAL = "pointer-events-none";

function sanitizeName(value: string): string {
  let s = value.replace(/[^a-zA-Z0-9_]/g, "");
  if (s.length > 0 && /^[0-9]/.test(s)) s = "_" + s;
  return s;
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const StructuredFieldRow = ({
  id,
  nodeId,
  fieldKey,
  row,
  index,
  typeOptions,
  isOpen,
  hasDuplicateName,
  cantDelete,
  disabled,
  onChange,
  onCopy,
  onDelete,
}: {
  id: string;
  nodeId: string;
  fieldKey: string;
  row: StructuredField;
  index: number;
  typeOptions: string[];
  isOpen: boolean;
  hasDuplicateName: boolean;
  cantDelete: boolean;
  disabled?: boolean;
  onChange: (index: number, patch: Partial<StructuredField>) => void;
  onCopy: (row: StructuredField) => void;
  onDelete: (index: number) => void;
}) => {
  const [localName, setLocalName] = useState<string | null>(null);
  const typeValue = typeValueOf(row);
  const typeMeta = fieldTypeMeta(typeValue);

  const shownName = row.name || `Field ${index + 1}`;
  const showNameTooltip = shownName.length > NAME_TOOLTIP_MIN_LENGTH;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem
        value={id}
        className="border rounded-md px-4 last:border-b first:border-t bg-[#FCFCFC]"
      >
        {/* `-mx-4 px-4` is not a no-op: it spans the card's own padding so
            hover survives the gutter beside the actions. */}
        <div className="group/row -mx-4 px-4 flex items-center gap-2 [&>h3]:grow [&>h3]:min-w-0">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
          <AccordionTrigger
            className="hover:no-underline py-3 grow items-center overflow-visible min-w-0"
            hideChevron
          >
            <div className="flex items-center justify-between w-full pr-2 min-w-0">
              <NoxusTooltip
                content={
                  showNameTooltip ? (
                    <span className="wrap-break-word">{shownName}</span>
                  ) : undefined
                }
                asChild
              >
                <span
                  className={cn(
                    "text-sm font-medium min-w-0 wrap-break-word",
                    showNameTooltip && "line-clamp-3"
                  )}
                >
                  {shownName}
                </span>
              </NoxusTooltip>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="grid items-center justify-items-end [&>*]:col-start-1 [&>*]:row-start-1">
                {(typeValue || typeOptions[0]) && (
                  <div className="h-6 flex items-center group-hover/row:invisible">
                    <Badge variant="outline">
                      {typeValue || typeOptions[0]}
                    </Badge>
                  </div>
                )}
                <div className="invisible group-hover/row:visible flex items-center gap-2 h-6">
                  <NoxusTooltip
                    content="Duplicate field"
                    classNameContent={TOOLTIP_NO_HOVER_STEAL}
                  >
                    <Button
                      variant="ghost"
                      size="iconsm"
                      className={ROW_ACTION_TRANSITION}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy(row);
                      }}
                      isDisabled={disabled}
                    >
                      <Copy className="size-4" strokeWidth={1.25} />
                    </Button>
                  </NoxusTooltip>
                  <NoxusTooltip
                    content={
                      cantDelete
                        ? "At least one field is required"
                        : "Delete field"
                    }
                    classNameContent={TOOLTIP_NO_HOVER_STEAL}
                  >
                    <Button
                      variant="ghost"
                      size="iconsm"
                      className={ROW_ACTION_TRANSITION}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(index);
                      }}
                      isDisabled={cantDelete || disabled}
                    >
                      <Trash className="size-4" strokeWidth={1.25} />
                    </Button>
                  </NoxusTooltip>
                </div>
              </div>

              <Button variant="ghost" size="iconsm">
                <ChevronUp
                  className={cn(
                    "transition-transform duration-200",
                    !isOpen ? "rotate-180" : ""
                  )}
                />
              </Button>
            </div>
          </AccordionTrigger>
        </div>

        <AccordionContent className="pb-4 pt-2 px-0.25 border-t">
          <div className="grid grid-cols-12 gap-3">
            <div className="flex flex-col gap-1.5 col-span-6">
              <label className="text-sm font-medium">Name</label>
              <NoxusInput
                value={localName !== null ? localName : row.name}
                customStyle="bg-background"
                placeholder="field_name"
                disable={disabled}
                hasError={hasDuplicateName}
                onChange={(e) => setLocalName(e.target.value)}
                onBlur={(e) => {
                  onChange(index, { name: sanitizeName(e.target.value) });
                  setLocalName(null);
                }}
              />
              {hasDuplicateName && (
                <span className="text-xs text-destructive">
                  Field names must be unique
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1.5 col-span-6">
              <label className="text-sm font-medium">Type</label>
              <SpotSelect
                name={`temp_${fieldKey}_${index}_type`}
                value={typeValue || typeOptions[0]}
                // SpotSelect emits the whole {value, label} option object —
                // unwrap so the stored row type is always a plain string.
                onChange={(selected: string | { value?: string } | null) => {
                  const nextType =
                    typeof selected === "string"
                      ? selected
                      : (selected?.value ?? typeOptions[0]);
                  const patch: Partial<StructuredField> = { type: nextType };
                  // List values are bind-only; a leftover typed literal would
                  // be stored but invisible, so drop it on switch.
                  if (
                    fieldTypeMeta(nextType).isList &&
                    !WHOLE_VAR_REF_PATTERN.test(row.value ?? "")
                  ) {
                    patch.value = "";
                  }
                  onChange(index, patch);
                }}
                options={typeOptions.map((opt) => ({
                  value: opt,
                  label: opt,
                }))}
                isDisabled={disabled}
                customItemContent={(option) => {
                  const Icon = option?.label
                    ? fieldTypeMeta(option.label).icon
                    : undefined;
                  return (
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="w-4 h-4" />}
                      <span>{option?.label}</span>
                    </div>
                  );
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5 col-span-12">
              <label className="text-sm font-medium">Value</label>
              <BindableInput
                nodeId={nodeId}
                value={row.value ?? ""}
                dataType={typeMeta.dataType}
                isList={typeMeta.isList}
                variableOnly={typeMeta.isList}
                inputName={fieldKey}
                disabled={disabled}
                placeholder={typeMeta.isList ? undefined : "Value"}
                onChange={(next) => onChange(index, { value: next })}
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
};

const JsonModeEditor = ({
  rows,
  typeOptions,
  onCancel,
  onSubmit,
  disabled,
}: {
  rows: StructuredField[];
  typeOptions: string[];
  onCancel: () => void;
  onSubmit: (rows: StructuredField[]) => void;
  disabled?: boolean;
}) => {
  const [jsonValue, setJsonValue] = useState(() =>
    JSON.stringify(
      rows.map((row) => ({ ...row, type: typeValueOf(row) || typeOptions[0] })),
      null,
      2
    )
  );
  const [errors, setErrors] = useState<string[]>([]);

  const validateAndSubmit = useCallback(() => {
    const validationErrors: string[] = [];

    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonValue);
    } catch (e) {
      setErrors([`Invalid JSON: ${(e as Error).message}`]);
      return;
    }

    if (!Array.isArray(parsed)) {
      setErrors(["Value must be an array of objects"]);
      return;
    }

    const allowedKeys = ["name", "type", "value"];
    const seenNames = new Set<string>();
    const result: StructuredField[] = [];

    parsed.forEach((entry, index) => {
      if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
        validationErrors.push(
          `Field ${index + 1}: Must be an object, got ${typeof entry}`
        );
        return;
      }

      const record = entry as Record<string, unknown>;
      const fieldLabel = record.name
        ? `"${record.name}" (${index + 1})`
        : `Field ${index + 1}`;

      Object.keys(record).forEach((key) => {
        if (!allowedKeys.includes(key)) {
          validationErrors.push(
            `${fieldLabel}: Unknown key "${key}". Allowed keys: ${allowedKeys.join(", ")}`
          );
        }
      });

      const name = sanitizeName(String(record.name ?? ""));
      if (!name) {
        validationErrors.push(`${fieldLabel}: Missing required key "name"`);
      } else {
        const lowerName = name.toLowerCase();
        if (seenNames.has(lowerName)) {
          validationErrors.push(`Field ${index + 1}: Duplicate name "${name}"`);
        } else {
          seenNames.add(lowerName);
        }
      }

      const type =
        record.type === undefined ? typeOptions[0] : String(record.type);
      if (!typeOptions.includes(type)) {
        validationErrors.push(
          `${fieldLabel}: Invalid value "${type}" for "type". Must be one of: ${typeOptions.join(", ")}`
        );
      }

      const rawValue = record.value;
      const value =
        rawValue === undefined || rawValue === null
          ? ""
          : typeof rawValue === "string"
            ? rawValue
            : JSON.stringify(rawValue);

      result.push({ name, type, value });
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    onSubmit(result);
  }, [jsonValue, typeOptions, onSubmit]);

  return (
    <div className="flex flex-col gap-3">
      <CodeInput
        value={jsonValue}
        onChange={(value: string) => setJsonValue(value)}
        isDisabled={disabled}
        placeholder="Enter JSON array..."
      />

      {errors.length > 0 && (
        <div className="bg-destructive/10 border border-destructive rounded-md p-3">
          <p className="font-medium text-sm mb-2 text-destructive">
            Validation Errors:
          </p>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {errors.map((error, i) => (
              <li key={i} className="text-destructive">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={disabled}>
          Cancel
        </Button>
        <Button onClick={validateAndSubmit} disabled={disabled}>
          Apply Changes
        </Button>
      </div>
    </div>
  );
};

export function StructuredFieldsField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  subtitle,
  description,
  typeOptions,
  disabled,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  subtitle?: string;
  description?: string;
  typeOptions: string[];
  disabled?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path) as StructuredField[] | undefined;
  const rows: StructuredField[] = useMemo(
    () => (Array.isArray(raw) ? raw : []),
    [raw]
  );
  const resolvedLabel = label || humanize(fieldKey);
  const options = useMemo(
    () => (typeOptions.length > 0 ? typeOptions : ["Text"]),
    [typeOptions]
  );
  const invalid = !!error;

  const [openItems, setOpenItems] = useState<string[]>([]);
  const [isJsonMode, setIsJsonMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const cantDelete = rows.length <= 1;

  const write = useCallback(
    (next: StructuredField[]) =>
      form.setValue(path, next, { shouldDirty: true, shouldTouch: true }),
    [form, path]
  );

  const duplicateNameIndices = useMemo(() => {
    const duplicates = new Set<number>();
    const seen = new Set<string>();
    rows.forEach((row, i) => {
      const name = row.name?.toLowerCase() || "";
      if (!name) return;
      if (seen.has(name)) duplicates.add(i);
      else seen.add(name);
    });
    return duplicates;
  }, [rows]);

  const ensureUniqueName = useCallback(
    (newName: string, currentIndex: number) => {
      if (!newName) return newName;
      const existing = new Set(
        rows
          .filter((_, i) => i !== currentIndex)
          .map((r) => r.name?.toLowerCase() || "")
          .filter(Boolean)
      );
      if (!existing.has(newName.toLowerCase())) return newName;
      let counter = 2;
      let unique = `${newName}_${counter}`;
      while (existing.has(unique.toLowerCase())) {
        counter++;
        unique = `${newName}_${counter}`;
      }
      return unique;
    },
    [rows]
  );

  const updateRow = useCallback(
    (index: number, patch: Partial<StructuredField>) => {
      const finalPatch =
        patch.name !== undefined
          ? { ...patch, name: ensureUniqueName(patch.name, index) }
          : patch;
      write(rows.map((r, i) => (i === index ? { ...r, ...finalPatch } : r)));
    },
    [rows, write, ensureUniqueName]
  );

  const removeRow = useCallback(
    (index: number) => {
      if (disabled || cantDelete) return;
      write(rows.filter((_, i) => i !== index));
    },
    [rows, write, disabled, cantDelete]
  );

  const copyRow = useCallback(
    (row: StructuredField) => {
      if (disabled) return;
      write([...rows, { ...row, name: ensureUniqueName(row.name, -1) }]);
    },
    [rows, write, disabled, ensureUniqueName]
  );

  const addRow = useCallback(() => {
    if (disabled) return;
    let i = rows.length + 1;
    const names = new Set(rows.map((r) => r.name));
    while (names.has(`field${i}`)) i += 1;
    write([...rows, { name: `field${i}`, type: options[0], value: "" }]);
  }, [rows, write, disabled, options]);

  const handleDragStart = useCallback(() => {
    setOpenItems([]);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || disabled) return;
      if (active.id !== over.id) {
        write(arrayMove(rows, Number(active.id), Number(over.id)));
      }
    },
    [rows, write, disabled]
  );

  const handleJsonSubmit = useCallback(
    (next: StructuredField[]) => {
      write(next);
      setIsJsonMode(false);
    },
    [write]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <div>
          <NoxusFormLabel
            label={resolvedLabel}
            tooltip={tooltip}
            name={fieldKey}
            noMargin
            isDisabled={disabled}
          />
          {subtitle ? (
            <NoxusFormHelperText>{subtitle}</NoxusFormHelperText>
          ) : null}
        </div>
        {!isJsonMode && rows.length > 0 && (
          <div className="flex gap-2 mb-2">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger>
                <Button variant="ghost" size="icon" disabled={disabled}>
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenItems(rows.map((_, index) => index.toString()));
                  }}
                >
                  <ChevronsUpDown />
                  Expand All
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenItems([]);
                  }}
                >
                  <ChevronsDownUp />
                  Collapse All
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) setIsJsonMode(true);
                  }}
                >
                  <Code className="w-4 h-4" />
                  JSON Mode
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {isJsonMode ? (
        <JsonModeEditor
          rows={rows}
          typeOptions={options}
          onCancel={() => setIsJsonMode(false)}
          onSubmit={handleJsonSubmit}
          disabled={disabled}
        />
      ) : (
        <>
          <div
            className={cn(
              invalid &&
                "rounded-md p-1.5 ring-1 ring-inset ring-warning-400/70"
            )}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={rows.map((_, idx) => idx.toString())}
                strategy={verticalListSortingStrategy}
              >
                <Accordion
                  type="multiple"
                  value={openItems}
                  onValueChange={setOpenItems}
                  className="flex flex-col gap-3"
                >
                  {rows.map((row, index) => (
                    <StructuredFieldRow
                      key={index}
                      id={index.toString()}
                      nodeId={nodeId}
                      fieldKey={fieldKey}
                      row={row}
                      index={index}
                      typeOptions={options}
                      isOpen={openItems.includes(index.toString())}
                      hasDuplicateName={duplicateNameIndices.has(index)}
                      cantDelete={cantDelete}
                      disabled={disabled}
                      onChange={updateRow}
                      onCopy={copyRow}
                      onDelete={removeRow}
                    />
                  ))}
                </Accordion>
              </SortableContext>
            </DndContext>
          </div>

          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="mt-2 w-full"
            onClick={addRow}
          >
            <Plus className="size-4" />
            Add Field
          </Button>
        </>
      )}
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );
}
